import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Label,
  Input,
  Button,
  Spinner,
  Alert,
} from "reactstrap";
import Loader from "../../../Components/Common/Loader";
import { useFormik } from "formik";
import * as Yup from "yup";

import {
  useCreateInventoryImageAttachmentMutation,
  useUpdateInventoryImageAttachmentMutation,
  useGetInventoryImageAttachmentByIdQuery,
  useGetInventoryImageAttachmentsQuery,
} from "slices/InventoryManagement/inventoryManagementSlice";

interface ProductImagePayload {
  name: string;
  description?: string;
  file: File | null;
}

interface ProductImagesEditProps {
  attachmentId?: number | string;
  compact?: boolean;
  onSaved?: () => void;
}

const ProductImagesEditView: React.FC<ProductImagesEditProps> = ({
  attachmentId,
  compact = true,
  onSaved,
}) => {
  const isEditMode = !!attachmentId;

  const [saveError, setSaveError] = useState<string | null>(null);

  const {
    data: attachmentData,
    isFetching,
    refetch,
  } = useGetInventoryImageAttachmentByIdQuery(
    String(attachmentId),
    { skip: !isEditMode }
  );

  const { refetch: refetchList } =
    useGetInventoryImageAttachmentsQuery(undefined);

  const [createAttachment, { isLoading: creating }] =
    useCreateInventoryImageAttachmentMutation();

  const [updateAttachment, { isLoading: updating }] =
    useUpdateInventoryImageAttachmentMutation();

  const formik = useFormik<ProductImagePayload>({
    enableReinitialize: true,
    initialValues: {
      name: "",
      description: "",
      file: null,
    },

    validationSchema: Yup.object({
      name: Yup.string().required("Please enter image name"),
      description: Yup.string().nullable(),
      file: Yup.mixed()
        .nullable()
        .test(
          "required-file",
          "Please select a file",
          function (value) {
            return isEditMode ? true : !!value;
          }
        ),
    }),

    onSubmit: async (values) => {
      setSaveError(null);

      try {
        const formData = new FormData();
        formData.append("name", values.name);

        // Send an empty string too, so an existing description can be cleared.
        formData.append("description", values.description || "");

        if (values.file) {
          formData.append("file", values.file);
        }

        if (isEditMode) {
          await updateAttachment({
            id: attachmentId,
            values: formData,
          }).unwrap();
        } else {
          await createAttachment(formData).unwrap();
        }

        refetchList();
        if (isEditMode) refetch();
        onSaved?.();
      } catch (err: any) {
        setSaveError(
          err?.data?.message ||
          err?.message ||
          "Failed to upload image"
        );
      }
    },
  });

  useEffect(() => {
    if (isEditMode && attachmentData) {
      formik.setValues({
        name: attachmentData.name ?? "",
        description: attachmentData.description ?? "",
        file: null, // file not pre-filled
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachmentData]);

  if (isEditMode && isFetching && compact) {
    return (
      <div className="text-center py-4">
        <Loader />
      </div>
    );
  }

  return (
    <>
      {saveError && (
        <Row>
          <Col lg={12}>
            <Alert color="danger">{saveError}</Alert>
          </Col>
        </Row>
      )}

      <Row>
        <Col lg={12}>
          <Card>
            {!compact && (
              <CardHeader>
                <h5 className="card-title mb-0">
                  {isEditMode ? "Edit Image" : "Add Image"}
                </h5>
              </CardHeader>
            )}

            <CardBody>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  formik.handleSubmit();
                }}
                encType="multipart/form-data"
              >
                {!isEditMode && (
                  <div className="mb-3">
                    <Label className="form-label">
                      Name <span className="text-danger">*</span>
                    </Label>
                    <Input
                      name="name"
                      value={formik.values.name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      invalid={!!(formik.touched.name && formik.errors.name)}
                    />
                    {formik.touched.name && formik.errors.name && (
                      <div className="text-danger mt-1">
                        {formik.errors.name}
                      </div>
                    )}
                  </div>
                )}

                {/* Description */}
                <div className="mb-3">
                  <Label className="form-label">Description</Label>
                  <Input
                    type="textarea"
                    name="description"
                    value={formik.values.description}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </div>

                {!isEditMode && (
                  <div className="mb-3">
                    <Label className="form-label">
                      File <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="file"
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        formik.setFieldValue(
                          "file",
                          e.currentTarget.files?.[0] || null
                        )
                      }
                      invalid={!!(formik.errors.file)}
                    />
                    {formik.errors.file && (
                      <div className="text-danger mt-1">
                        {formik.errors.file}
                      </div>
                    )}
                  </div>
                )}

                {/* Submit */}
                <div className="text-end">
                  <Button
                    color="primary"
                    type="submit"
                    disabled={creating || updating}
                  >
                    {creating || updating ? (
                      <>
                        <Spinner size="sm" className="me-1" />
                        Saving...
                      </>
                    ) : isEditMode ? (
                      "Update"
                    ) : (
                      "Upload"
                    )}
                  </Button>
                </div>
              </form>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default ProductImagesEditView;
